import React, { useState, useRef, useEffect, useMemo } from "react";
import type { TeamMember } from "@/hooks/useTeam";
import { X, ChevronDown, Check, Search, Users } from "lucide-react";
import { Input } from "../ui/input";
import { useTranslation } from "react-i18next";

interface DistributeEquallySelectionProps {
  options?: TeamMember[];
  placeholder?: string;
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  maxHeight?: string;
  classNames?: string;
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

const DistributeEquallySelection: React.FC<DistributeEquallySelectionProps> = ({
  options = [],
  placeholder = "Selecionar membros da equipe",
  selectedIds,
  onSelectionChange,
  maxHeight = "max-h-60",
  classNames = "",
}) => {
  const { t } = useTranslation("automations");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedMembers = useMemo(
    () => options.filter((m) => selectedIds.includes(m.id)),
    [options, selectedIds]
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase().trim();
    return options.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  const allFilteredSelected = useMemo(
    () =>
      filteredOptions.length > 0 &&
      filteredOptions.every((m) => selectedIds.includes(m.id)),
    [filteredOptions, selectedIds]
  );

  const handleToggle = (memberId: string) => {
    if (selectedIds.includes(memberId)) {
      onSelectionChange(selectedIds.filter((id) => id !== memberId));
    } else {
      onSelectionChange([...selectedIds, memberId]);
    }
  };

  const handleRemove = (memberId: string) => {
    onSelectionChange(selectedIds.filter((id) => id !== memberId));
  };

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredOptions.map((m) => m.id));
      onSelectionChange(selectedIds.filter((id) => !filteredIds.has(id)));
    } else {
      const newIds = new Set([
        ...selectedIds,
        ...filteredOptions.map((m) => m.id),
      ]);
      onSelectionChange(Array.from(newIds));
    }
  };

  const toggleOpen = () => {
    setIsOpen((o) => !o);
    if (!isOpen) setSearchQuery("");
  };

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", onClickOutside);
    else document.removeEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`w-full relative ${classNames}`}>
      <div
        className="w-full min-h-[44px] rounded-xl bg-white border border-[#E5E7EB] px-3 py-2 cursor-pointer flex items-center gap-2 flex-wrap transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46]"
        onClick={toggleOpen}
      >
        {selectedMembers.length === 0 ? (
          <span className="text-[#94A3B8] dark:text-[#71717A] text-sm">{placeholder}</span>
        ) : (
          selectedMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-1.5 bg-[#DBEAFE] text-[#2563EB] rounded-full px-2 py-0.5 text-[12px] dark:bg-blue-500/20 dark:text-[#60A5FA]"
            >
              <div className="w-5 h-5 rounded-full bg-[#2563EB] flex items-center justify-center text-[9px] font-medium text-white flex-shrink-0">
                {getInitials(member.name)}
              </div>
              <span className="font-medium truncate max-w-[100px]">
                {member.name || member.email}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(member.id);
                }}
                type="button"
                aria-label={`Remover ${member.name}`}
                className="hover:text-[#DC2626] transition-colors duration-200 dark:hover:text-[#F87171]"
              >
                <X size={12} />
              </button>
            </div>
          ))
        )}
        <ChevronDown
          size={16}
          className={`text-[#64748B] dark:text-[#71717A] ml-auto transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-md z-50 flex flex-col dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
          <div className="p-2 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] dark:text-[#A1A1AA]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar membro..."
                className="pl-8 h-8 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] text-sm placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {options.length > 1 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#334155] hover:bg-[#F1F5F9] border-b border-[#E5E7EB] transition-colors duration-200 dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46] dark:border-[#3F3F46]"
            >
              <Users size={14} />
              <span>
                {allFilteredSelected ? "Desmarcar todos" : "Selecionar todos"}
              </span>
              {selectedIds.length > 0 && (
                <span className="ml-auto text-xs text-[#64748B] dark:text-[#71717A]">
                  {selectedIds.length}/{options.length}
                </span>
              )}
            </button>
          )}

          <div className={`${maxHeight} overflow-y-auto`}>
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-[#64748B] dark:text-[#71717A] text-sm text-center">
                {t("campaigns.noMemberFound")}
              </div>
            ) : (
              filteredOptions.map((member) => {
                const isSelected = selectedIds.includes(member.id);
                return (
                  <div
                    key={member.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-200 ${
                      isSelected
                        ? "bg-[#EEF4FF] hover:bg-[#DBEAFE] dark:bg-blue-500/15 dark:hover:bg-blue-500/20"
                        : "hover:bg-[#F1F5F9] dark:hover:bg-[#3F3F46]"
                    }`}
                    onClick={() => handleToggle(member.id)}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
                      {getInitials(member.name)}
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#0F172A] truncate dark:text-[#FAFAFA]">
                        {member.name || "Sem nome"}
                      </p>
                      <p className="text-[12px] text-[#64748B] truncate dark:text-[#71717A]">
                        {member.email}
                      </p>
                    </div>
                    {isSelected && (
                      <Check size={16} className="text-[#2563EB] dark:text-[#60A5FA] flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributeEquallySelection;
